import { Link, useParams } from "react-router-dom";
import type { OfficeProductDefinition } from "@apf/framework-config";
import { businessModulePath, type BusinessModule } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import { OfficeProductCards } from "../components/OfficeProductCards";
import { AbsenceBlock, ArrowLinkLabel, PageHeader, SectionHeading, StatusLegend, StatusMark, ordinal } from "../components/ui";
import { usePageTitle } from "./support";

function replaceName(template: string, name: string): string {
  return template.replace("{name}", name);
}

function OfficeProductHero({ product }: { product: OfficeProductDefinition }) {
  const copy = useCopy();
  const ProductIcon = iconFor(product.icon);
  usePageTitle(copy(product.nameKey));
  return (
    <div className="apf-office-product-hero">
      <section className="apf-office-product-hero__main">
        <p className="apf-office-product-hero__program">{copy(product.programKey)}</p>
        <p className="apf-office-product-hero__identity">
          <ProductIcon size={24} />
          <span>{copy(product.formatKey)}</span>
        </p>
        <h1>{copy(product.nameKey)}</h1>
        <p className="apf-office-product-hero__tagline">{copy(product.taglineKey)}</p>
        <p className="apf-office-product-hero__summary">{copy(product.summaryKey)}</p>
      </section>
      <aside className="apf-office-product-hero__aside" aria-label={replaceName(copy("office.page.hero.aside-label", "{name} product position"), copy(product.nameKey))}>
        <p>{copy(product.thesisKey)}</p>
        <div className="apf-office-product-hero__delivery">
          <span>{copy(product.deliveryKey)}</span>
        </div>
      </aside>
    </div>
  );
}

function BusinessModuleRow({ businessModule, position }: { businessModule: BusinessModule; position: number }) {
  const copy = useCopy();
  return (
    <li className="apf-ruled-index__row">
      <Link className="apf-ruled-index__body apf-ruled-index__link" to={businessModulePath(businessModule.domainWorkblockId, businessModule.id)}>
        <span className="apf-ruled-index__ordinal">{ordinal(position)}</span>
        <div className="apf-ruled-index__heading">
          <h3 className="apf-ruled-index__name">{copy(businessModule.labelKey)}</h3>
          {copy(businessModule.descriptorKey) !== copy(businessModule.labelKey) ? <span className="apf-ruled-index__kicker">{copy(businessModule.descriptorKey)}</span> : null}
        </div>
        <p className="apf-ruled-index__description">{copy(businessModule.outcomeKey)}</p>
        <div className="apf-ruled-index__meta">
          <StatusMark value={businessModule.implementationStatus} />
          <ArrowLinkLabel>{copy(businessModule.toolKey)}</ArrowLinkLabel>
        </div>
      </Link>
    </li>
  );
}

export function OfficeCapabilitiesPage() {
  const copy = useCopy();
  return (
    <div className="apf-page apf-register-reference apf-office-catalogue-page">
      <PageHeader
        eyebrow={copy("office.catalogue.eyebrow", "Accenture GroundWorks capabilities")}
        title={copy("office.catalogue.title", "Composer and Conductor")}
        description={copy("office.catalogue.description", "Native DOCX and XLSX capabilities for people, agents and Workblocks acting inside explicit authority, delivered by Beyond Work execution rails.")}
      />
      <section>
        <SectionHeading
          title={copy("office.catalogue.cards-heading", "Choose the artifact at the centre of the work")}
          description={copy("office.catalogue.cards-description", "Open the full product specifications for role responsibilities, agentic counterparts, policy-bounded execution, rich-format coverage and product requirements.")}
        />
        <OfficeProductCards />
      </section>
    </div>
  );
}

export function OfficeProductPage() {
  const { productId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const products = snapshot.content.officeProducts ?? [];
  const product = products.find((candidate) => candidate.id === productId);

  if (!product) {
    return (
      <div className="apf-page apf-register-reference">
        <AbsenceBlock
          eyebrow={copy("absence.capability.eyebrow", "Office capability reference")}
          title={copy("absence.capability.title", "Capability not found")}
          support={copy("absence.capability.support", "This Office capability is not part of the current Accenture GroundWorks program.")}
          headingLevel={1}
          holds={[
            { label: copy("absence.holding.office-capabilities", "Office capabilities"), count: products.length },
            { label: copy("absence.holding.business-modules", "Business modules"), count: snapshot.businessModuleRegistry.businessModules.length },
            { label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length },
          ]}
          routes={<Link to="/capabilities">{copy("absence.capability.route", "Return to Office capabilities")}</Link>}
        />
      </div>
    );
  }

  const companion = products.find((candidate) => candidate.id === product.companionProductId);
  const businessModules = product.relatedBusinessModuleIds.flatMap((id) => {
    const businessModule = snapshot.businessModuleRegistry.businessModules.find((candidate) => candidate.id === id);
    return businessModule ? [businessModule] : [];
  });

  return (
    <div className="apf-page apf-register-reference apf-office-product-page">
      <Link className="apf-button apf-button--quiet apf-back-link" to="/capabilities">
        {copy("office.page.back", "Back to Office capabilities")}
      </Link>

      <OfficeProductHero product={product} />

      <nav className="apf-office-product-index" aria-label={replaceName(copy("office.page.index-label", "{name} specification sections"), copy(product.nameKey))}>
        <a href="#agentic-counterpart">{copy("office.page.index.agentic-counterpart", "Agentic counterpart")}</a>
        <a href="#role-value">{copy("office.page.index.role-value", "Role value")}</a>
        <a href="#native-capability">{copy("office.page.index.native-capability", "Native capability")}</a>
        <a href="#autonomous-execution">{copy("office.page.index.autonomous-execution", "Actions inside authority")}</a>
        <a href="#workblock-reach">{copy("office.page.index.workblock-reach", "Workblock reach")}</a>
        <a href="#competitive-difference">{copy("office.page.index.competitive-difference", "Competitive difference")}</a>
        <a href="#product-contract">{copy("office.page.index.product-contract", "Product contract")}</a>
      </nav>

      <section className="apf-page-section" id="agentic-counterpart">
        <SectionHeading title={copy(product.actionsHeadingKey)} description={copy(product.actionSectionDescriptionKey)} />
        <div className="apf-data-table-wrap" role="region" aria-label={copy(product.actionsHeadingKey)} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive apf-office-action-table">
            <thead>
              <tr>
                <th scope="col">{copy("office.shared.actions.human-heading")}</th>
                <th scope="col">{copy("office.shared.actions.agent-heading")}</th>
              </tr>
            </thead>
            <tbody>
              {product.actionPairs.map((action) => (
                <tr key={action.id}>
                  <td data-label={copy("office.shared.actions.human-heading")}>{copy(action.humanActionKey)}</td>
                  <td data-label={copy("office.shared.actions.agent-heading")}>{copy(action.agentActionKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="apf-page-section" id="role-value">
        <SectionHeading title={copy(product.rolesHeadingKey)} description={copy(product.rolesDescriptionKey)} />
        <div className="apf-office-role-grid">
          {product.roleJobs.map((role) => (
            <article className="apf-office-role" key={role.id}>
              <p className="apf-office-role__role">{copy(role.roleKey)}</p>
              <h3>{copy(role.questionKey)}</h3>
              <p>{copy(role.valueKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="apf-page-section" id="native-capability">
        <SectionHeading title={copy(product.capabilitiesHeadingKey)} description={copy(product.capabilitiesDescriptionKey)} />
        <dl className="apf-office-capability-ledger">
          {product.capabilityFamilies.map((family) => (
            <div key={family.id}>
              <dt>{copy(family.labelKey)}</dt>
              <dd>{copy(family.valueKey)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="apf-page-section" id="autonomous-execution">
        <SectionHeading title={copy(product.autonomyHeadingKey)} description={copy(product.autonomyDescriptionKey)} />
        <ol className="apf-office-autonomy-sequence">
          {product.autonomySteps.map((step, index) => (
            <li key={step.id}>
              <span>{ordinal(index + 1)}</span>
              <div><h3>{copy(step.labelKey)}</h3><p>{copy(step.descriptionKey)}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="apf-page-section" id="workblock-reach">
        <SectionHeading title={copy(product.workblocksHeadingKey)} description={copy(product.workblocksDescriptionKey)} />
        <StatusLegend />
        <ol className="apf-ruled-index apf-ruled-index--workblocks apf-office-workblock-index">
          {businessModules.map((businessModule) => (
            <BusinessModuleRow
              key={businessModule.id}
              businessModule={businessModule}
              position={snapshot.businessModuleRegistry.businessModules.findIndex((candidate) => candidate.id === businessModule.id) + 1}
            />
          ))}
        </ol>
      </section>

      <section className="apf-page-section" id="competitive-difference">
        <SectionHeading title={copy(product.differentiatorsHeadingKey)} description={copy(product.differentiatorsDescriptionKey)} />
        <div className="apf-office-difference-grid">
          {product.differentiators.map((difference) => (
            <article className="apf-office-difference" key={difference.id}>
              <h3>{copy(difference.labelKey)}</h3>
              <p>{copy(difference.descriptionKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="apf-page-section" id="product-contract">
        <SectionHeading title={copy(product.requirementsHeadingKey)} description={copy(product.requirementsDescriptionKey)} />
        <ol className="apf-office-requirements">
          {product.requirementKeys.map((key, index) => (
            <li key={key}>
              {/* The label is prose and the ordinal is a machine value, so
                * only the ordinal keeps the mono voice. */}
              <span>
                {copy("office.shared.requirements.item-label")}{" "}
                <span className="apf-office-requirements__ordinal">{ordinal(index + 1)}</span>
              </span>
              <p>{copy(key)}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="apf-office-product-close" aria-labelledby="office-close-title">
        <div>
          <p>{copy(product.programKey)}</p>
          <h2 id="office-close-title">{copy(product.closingKey)}</h2>
          <span>{copy(product.deliveryKey)}</span>
        </div>
        {companion ? (
          <Link className="apf-office-product-close__companion" to={companion.route}>
            <span>{copy("office.shared.companion.heading")}</span>
            <strong>{copy(companion.nameKey)}</strong>
            <small>{copy("office.shared.companion.description")}</small>
            <ArrowLinkLabel>{copy(product.ctaKey)}</ArrowLinkLabel>
          </Link>
        ) : null}
      </section>
    </div>
  );
}
